<template>
  <div class="materials-quote-form">
    <div class="form-header mb-4">
      <h4>{{ isEditing ? 'Edit Materials Quote' : 'Create Materials Quote' }}</h4>
      <p class="text-muted">{{ isEditing ? `Quote #${quote.shortId}` : 'Create a new materials quote' }}</p>
    </div>

    <form @submit.prevent="handleSubmit">
      <div class="row">
        <div class="col">
          <div class="inputs">
            <div class="section-header">Quote Details</div>
            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label for="title">Quote Title</label>
                  <b-form-input
                    id="title"
                    v-model="form.title"
                    type="text"
                    required
                    :disabled="submitting"
                    placeholder="Enter quote title"
                  ></b-form-input>
                </div>
              </div>
              <div class="col">
                <div class="form-group">
                  <label for="status">Status</label>
                  <b-form-select
                    id="status"
                    v-model="form.status"
                    :options="statusOptions"
                    :disabled="submitting"
                  ></b-form-select>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label for="amount">Quote Amount ($) <span class="text-muted">(Optional)</span></label>
                  <b-form-input
                    id="amount"
                    v-model="form.amount"
                    type="number"
                    step="0.01"
                    min="0"
                    :disabled="submitting"
                    placeholder="0.00"
                  ></b-form-input>
                </div>
              </div>
              <div class="col">
                <div class="form-group">
                  <label for="expirationDate">Expiration Date <span class="text-muted">(Optional)</span></label>
                  <b-form-input
                    id="expirationDate"
                    v-model="form.expirationDate"
                    type="date"
                    :disabled="submitting"
                  ></b-form-input>
                </div>
              </div>
            </div>

            <div class="section-header">QuickBooks Quote Numbers</div>
            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label for="quoteNumbers">Quote Numbers (Optional)</label>
                  <help-icon text="Enter QuickBooks quote numbers, one per line"></help-icon>
                  <b-form-textarea
                    id="quoteNumbers"
                    v-model="quoteNumbersText"
                    rows="3"
                    :disabled="submitting"
                    placeholder="Enter QuickBooks quote numbers, one per line"
                  ></b-form-textarea>
                </div>
              </div>
            </div>

            <div class="section-header">Description & Information</div>
            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label for="description">Description</label>
                  <b-form-textarea
                    id="description"
                    v-model="form.description"
                    rows="6"
                    :disabled="submitting"
                    placeholder="Copy and paste quote information, materials list, or any relevant details here"
                  ></b-form-textarea>
                </div>
              </div>
            </div>

            <div class="section-header">Customer Details</div>
            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label for="customerName">Customer Name</label>
                  <div class="hidden-customer-name-wrapper">
                    <vue-typeahead-bootstrap
                      id="customerName"
                      v-model="form.customer.name"
                      :data="customerStore.customerNames.value"
                      :required="true"
                      input-name="customerName"
                      label="Customer Name"
                      @hit="setCustomer"
                    />
                    <input
                      type="text"
                      v-model="form.customer.name"
                      required
                      tabindex="-1"
                      class="hidden-customer-name"
                    />
                  </div>
                </div>
              </div>
              <div class="col">
                <div class="form-group">
                  <label for="customerEmail">Customer Email (Optional)</label>
                  <b-form-input
                    id="customerEmail"
                    type="email"
                    v-model="form.customer.email"
                    :disabled="submitting"
                  ></b-form-input>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <div class="form-group">
                  <label for="customerPhone">Customer Phone (Optional)</label>
                  <b-form-input
                    id="customerPhone"
                    type="text"
                    v-model="form.customer.phone"
                    :disabled="submitting"
                  ></b-form-input>
                </div>
              </div>
              <div class="col">
                <div class="form-group">
                  <label for="customerCompany">Company (Optional)</label>
                  <b-form-input
                    id="customerCompany"
                    type="text"
                    v-model="form.customer.company"
                    :disabled="submitting"
                  ></b-form-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions d-flex justify-content-between">
        <button
          type="button"
          class="btn btn-secondary"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
        
        <div>
          <button
            v-if="!isEditing"
            type="submit"
            class="btn btn-outline-primary me-2"
            @click="saveAsDraft"
            :disabled="loading"
          >
            Save as Draft
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="loading"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isEditing ? 'Update Quote' : 'Create Quote' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap'
import HelpIcon from '@/components/HelpIcon.vue'
import _ from 'lodash'

export default {
  name: 'MaterialsQuoteForm',
  components: {
    VueTypeaheadBootstrap,
    HelpIcon
  },
  inject: ['customerStore'],
  props: {
    quote: {
      type: Object,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    prefilledCustomer: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      quoteNumbersText: '',
      statusOptions: [
        { value: 'DRAFT', text: 'Draft' },
        { value: 'OPEN', text: 'Open' },
        { value: 'SOLD', text: 'Sold' },
        { value: 'REJECTED', text: 'Rejected' },
        { value: 'EXPIRED', text: 'Expired' }
      ],
      form: {
        title: '',
        description: '',
        status: 'DRAFT',
        amount: null,
        expirationDate: '',
        quoteNumbers: [],
        customer: {
          id: '',
          name: '',
          company: '',
          email: '',
          phone: ''
        }
      }
    }
  },
  mounted() {
    if (this.isEditing && this.quote) {
      this.loadQuoteData()
    } else if (this.prefilledCustomer) {
      // Pre-fill customer data when creating a new quote from customer profile
      this.form.customer = {
        id: this.prefilledCustomer.id || '',
        name: this.prefilledCustomer.name || '',
        company: this.prefilledCustomer.company || '',
        email: this.prefilledCustomer.email || '',
        phone: this.prefilledCustomer.phone || ''
      }
    }
  },
  methods: {
    loadQuoteData() {
      this.form = {
        title: this.quote.title || '',
        description: this.quote.description || '',
        status: this.quote.status || 'DRAFT',
        amount: this.quote.amount || null,
        expirationDate: this.quote.expirationDate ? this.formatDateForInput(this.quote.expirationDate) : '',
        quoteNumbers: this.quote.quoteNumbers || [],
        customer: {
          id: this.quote.customer?.id || '',
          name: this.quote.customer?.name || '',
          company: this.quote.customer?.company || '',
          email: this.quote.customer?.email || '',
          phone: this.quote.customer?.phone || ''
        }
      }
      
      // Convert quote numbers array to text
      this.quoteNumbersText = this.form.quoteNumbers.join('\n')
    },

    async handleSubmit() {
      try {
        this.loading = true
        this.submitting = true
        
        // Convert quote numbers text to array
        this.form.quoteNumbers = this.quoteNumbersText
          .split('\n')
          .map(num => num.trim())
          .filter(num => num.length > 0)
        
        if (this.isEditing) {
          await this.updateQuote()
        } else {
          await this.createQuote()
        }
      } catch (error) {
        console.error('Error saving quote:', error)
        this.$bvToast.toast('Error saving materials quote', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loading = false
        this.submitting = false
      }
    },

    async saveAsDraft() {
      this.form.status = 'DRAFT'
      await this.handleSubmit()
    },

    async createQuote() {
      const response = await axios.post('/services/v2/materials-quotes', this.form)
      
      this.$bvToast.toast('Materials quote created successfully', {
        title: 'Success',
        variant: 'success'
      })
      
      this.$emit('saved', response.data.quote)
    },

    async updateQuote() {
      const response = await axios.put(`/services/v2/materials-quotes/${this.quote.shortId}`, this.form)
      
      this.$bvToast.toast('Materials quote updated successfully', {
        title: 'Success',
        variant: 'success'
      })
      
      this.$emit('saved', response.data.quote)
    },

    setCustomer() {
      let customer = _.find(this.customerStore.customers, customer => {
        return customer.name === this.form.customer.name
      })
      if (customer) {
        this.form.customer.id = customer._id || customer.id || ''
        this.form.customer.email = this.extractEmail(customer.email) || ''
        this.form.customer.phone = customer.phone || ''
        this.form.customer.company = customer.company || ''
      }
    },

    extractEmail(emailString) {
      if (!emailString) return ''
      
      // Check if email is in format "Name <email@domain.com>"
      const emailMatch = emailString.match(/<([^>]+)>/)
      if (emailMatch) {
        return emailMatch[1]
      }
      
      // If no angle brackets, check if it's already a clean email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (emailRegex.test(emailString.trim())) {
        return emailString.trim()
      }
      
      // If neither format matches, return the original string
      return emailString
    },

    formatDateForInput(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-checkbox {
  display: inline-block;
  margin-left: 4px;
}

.section-header {
  font-size: 18px;
  text-decoration: underline;
  margin-bottom: 6px;
  font-weight: 500;
}

.hidden-customer-name-wrapper {
  position: relative;
}

.hidden-customer-name {
  position: absolute;
  z-index: -1;
  bottom: 0;
  left: 0;
}

.form-actions {
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
  margin-top: 2rem;
}
</style>
